
import { format } from "date-fns";
import { DocumentProps } from "@/hooks/admin/identity-docs/useSubmittedDocuments";
import { Badge } from "@/components/ui/badge";
import { InfoIcon } from "lucide-react";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface DocumentMetadataProps {
  document: DocumentProps;
}

export function DocumentMetadata({ document }: DocumentMetadataProps) {
  // Check if document is expired
  const isExpired = document.expiry_date && new Date(document.expiry_date) < new Date();
  
  return (
    <div className="space-y-1 text-sm">
      {document.document_number && (
        <div className="flex items-center">
          <span className="font-semibold mr-1">ID#:</span> 
          {document.document_number}
        </div>
      )}
      
      <div className="flex flex-wrap gap-2 mt-1">
        {document.issue_date && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="text-xs">
                  Issued: {format(new Date(document.issue_date), "dd/MM/yyyy")}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>Document issue date</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        {document.expiry_date && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant={isExpired ? "destructive" : "outline"} className="text-xs">
                  {isExpired ? "Expired: " : "Expires: "}
                  {format(new Date(document.expiry_date), "dd/MM/yyyy")}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isExpired ? "Document is expired" : "Document expiry date"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        {isExpired && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="inline-flex text-destructive">
                  <InfoIcon className="h-4 w-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>This document has expired and may not be valid for verification</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        {document.primary_document && (
          <Badge variant="secondary" className="text-xs">Primary</Badge>
        )}
      </div>
    </div>
  );
}
